/**
 * Phase 9B.2 — Mobile "Meer" bottom-sheet drawer.
 *
 * Reachable from the last slot of the mobile bottom-nav. Contains:
 *
 *   • Secondary planner destinations (Team vandaag / Snel toewijzen)
 *   • Personal work destinations (my-leave, my-swaps, my-availability)
 *   • Communication (news, notifications, handbook)
 *   • Personal account (profile, preferences, settings/billing when
 *     capability allows)
 *   • Logout
 *
 * Full-height sheet slides from the bottom with a focus trap on the
 * primary close button and Escape-to-close support.
 */
import { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, LogOut } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import { isHandbookUnread } from "@/lib/handbookVersion";

export default function MoreDrawer({ open, onClose, groups }) {
  const { t } = useI18n();
  const { logout } = useAuth();
  const closeRef = useRef(null);
  const handbookNew = isHandbookUnread();

  // Escape closes the sheet; focus lands on the close button on open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    const t = setTimeout(() => closeRef.current?.focus(), 40);
    return () => { window.removeEventListener("keydown", onKey); clearTimeout(t); };
  }, [open, onClose]);

  const handleLogout = async () => { onClose?.(); await logout(); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="md:hidden fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-end"
          onClick={onClose}
          data-testid="more-drawer-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t("more_nav")}
        >
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 320 }}
            className="w-full max-h-[85vh] bg-[hsl(var(--surface))] rounded-t-3xl p-5 pb-8 flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="more-drawer"
          >
            <div className="flex items-center justify-between pb-3 mb-2 border-b hairline">
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[hsl(var(--text-soft))]">Rostr</div>
                <h2 className="type-h3 mt-0.5">{t("more_nav")}</h2>
              </div>
              <button
                ref={closeRef}
                data-testid="more-drawer-close"
                className="w-11 h-11 grid place-items-center rounded-xl hover:bg-[hsl(var(--muted))] focus-visible-ring"
                onClick={onClose}
                aria-label={t("close") || "Sluiten"}
              >
                <X size={18} strokeWidth={1.9} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto -mx-1 px-1">
              {groups.map((group, gi) => (
                <div key={group.key || gi} className={gi > 0 ? "mt-4" : ""}>
                  {group.section && (
                    <div className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.14em] font-bold text-[hsl(var(--text-soft))]">
                      {group.section}
                    </div>
                  )}
                  <div className="flex flex-col gap-0.5">
                    {group.items.map((n) => (
                      <NavLink
                        key={n.to}
                        to={n.to}
                        onClick={onClose}
                        data-testid={n.testid}
                        className={({ isActive }) => `nav-link focus-visible-ring !py-3 ${isActive ? "active" : ""}`}
                        aria-label={n.label}
                      >
                        <span className="nav-icon-bg"><n.icon size={16} strokeWidth={1.85} /></span>
                        <span className="flex-1 truncate">{n.label}</span>
                        {n.handbookNewBadge && handbookNew && (
                          <span
                            className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))] text-white uppercase tracking-wider"
                            data-testid="more-handbook-new-badge"
                          >{t("nav_new_badge")}</span>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
            </nav>

            <button
              data-testid="more-drawer-logout"
              onClick={handleLogout}
              className="nav-link !py-3 mt-3 border-t hairline focus-visible-ring"
            >
              <span className="nav-icon-bg"><LogOut size={15} strokeWidth={1.85} /></span>
              <span className="flex-1 truncate">{t("logout")}</span>
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
